import ApiService from '@/common/api.service'

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param attachment Object
 *
 * @return Object
 */
function transform (attachment) {
  return {
    id: attachment.id,
    description: attachment.description,
    filename: attachment.filename,
    size_bytes: attachment.size_bytes,
    type: attachment.type,
    upload_status: attachment.upload_status,
    upload_status_text: attachment.upload_status_text,
    url: attachment.url
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
export function defaults () {
  return {
    isEditing: false,
    isAdding: false,
    isDeleting: false,
    warning: '',
    attachments: []
  }
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns an array of attachments
   *
   * @return Array
   */
  attachments (state) {
    return state.attachments
  },

  /**
   * Returns the current form warning.
   *
   * @param state Object
   *
   * @return string
   */
  warning (state) {
    return state.warning
  }
}

export const actions = {
  /**
   * API request to create a new attachment. The payload must be in the format:
   * { url: String, data: Object }
   *
   * @return Promise
   */
  post (context, payload) {
    return ApiService
      .post(payload.url, transform(payload.data))
      .then((response) => {
        context.commit('add', response.data.data)
        return response
      })
  },

  /**
   * API request to delete an existing attachment. The payload must be in the format:
   * { url: String, data: Object  }
   *
   * @return Promise
   */
  delete (context, payload) {
    return ApiService
      .delete(payload.url)
      .then((response) => {
        context.commit('removeById', payload.data.id)
        return response
      })
  }
}

export const mutations = {
  /**
   * Set an array of attachments.
   *
   * @param state       Object
   * @param attachments Array
   */
  setAttachments (state, attachments) {
    state.attachments = attachments
  },

  /**
   * Add a new attachment.
   *
   * @param state      Object
   * @param attachment Object
   */
  add (state, attachment) {
    state.attachments.push(attachment)
  },

  /**
   * Find a attachment by index in the array of attachments and updates it.
   *
   * @param state   Object
   * @param attachment Object
   * @param index   Number
   */
  updateByIndex (state, {attachment, index}) {
    state.attachments[index] = attachment
  },

  /**
   * Updates the last attachment that was added.
   *
   * @param state   Object
   * @param attachment Object
   */
  updateNewest (state, attachment) {
    state.attachments[state.attachments.length - 1] = attachment
  },

  /**
   * Find a attachment by id and remove it.
   *
   * @param state Object
   * @param id    Number
   */
  removeById (state, id) {
    let index = state.attachments.findIndex(element => element.id === id)
    state.attachments.splice(index, 1)
  },

  /**
   * Remove a attachment by index in the array of attachments.
   *
   * @param state Object
   * @param index Number
   */
  removeByIndex (state, index) {
    state.attachments.splice(index, 1)
  },

  /**
   * Clears the attachments from the state.
   *
   * @param state Object
   */
  purge (state) {
    state.attachments = []
  },

  /**
   * Sets the isAdding form state.
   *
   * @param state   Object
   * @param isAdding Boolean
   */
  setIsAdding (state, isAdding) {
    state.isAdding = isAdding
  },

  /**
   * Sets the isEditing form state.
   *
   * @param state     Object
   * @param isEditing Boolean
   */
  setIsEditing (state, isEditing) {
    state.isEditing = isEditing
  },

  /**
   * Sets the isDeleting form state.
   *
   * @param state      Object
   * @param isDeleting Boolean
   */
  setIsDeleting (state, isDeleting) {
    state.isDeleting = isDeleting
  },

  /**
   * Sets the form warning.
   *
   * @param state      Object
   * @param warning Boolean
   */
  setWarning (state, warning) {
    state.warning = warning
  },

  /**
   * Resets the form's state.
   *
   * @param state      Object
   */
  resetFormState (state) {
    state.isAdding = false
    state.isEditing = false
    state.isDeleting = false
    state.warning = ''
  }
}

export default {
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/attachments/attachmentsModule.js